// ============================================
// Path: /frontend/src/components/layout/AdminSidebar.jsx
// Purpose: Admin layout with collapsible sidebar navigation
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.jsx';
import {
  Sidebar,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel
} from '../ui/sidebar';
import {
  LayoutDashboard,
  LogOut,
  Settings,
  Share2,
  List,
  FileText,
  BarChart,
  MessageSquare,
  Handshake,
  CreditCard,
  Megaphone,
  Database,
  Mail,
  Users,
  Loader2

} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { DollarSign } from 'lucide-react';

// ========================================
// MAIN COMPONENT
// ========================================
export default function AdminSidebar({ children }) {
  console.log('🎨 [AdminSidebar] Component mounted');

  // ========================================
  // HOOKS & STATE
  // ========================================
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  // ========================================
  // AUTH CHECK
  // ========================================
  useEffect(() => {
    console.log('🔵 [AdminSidebar] Auth check...', { user, loading });
    
    if (!loading && (!user || user.role !== 'ADMIN')) {
      console.log('❌ [AdminSidebar] Unauthorized access, redirecting to login');
      navigate('/login?redirect=/admin');
    }
  }, [user, loading, navigate]);

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleLogout = async () => {
    console.log('👤 [AdminSidebar] Logout clicked');
    try {
      await logout();
      console.log('✅ [AdminSidebar] Logout successful');
      navigate('/login');
    } catch (err) {
      console.error('❌ [AdminSidebar] Logout error:', err);
    }
  };

  // ========================================
  // LOADING STATE
  // ========================================
  if (loading || !user || user.role !== 'ADMIN') {
    console.log('⏳ [AdminSidebar] Loading or checking auth...');
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // ========================================
  // RENDER
  // ========================================
  console.log('🎨 [AdminSidebar] Rendering admin layout for:', user.email);

  return (
    <SidebarProvider>
      <Sidebar>
        <SidebarHeader>
          <div className="flex items-center gap-2">
            <img src="/images/logo.png" alt="MotoHome Finder" className="h-7 w-7 sm:h-8 sm:w-8" />
            <span className="text-lg font-semibold">MotoHome Admin</span>
          </div>
        </SidebarHeader>
        <SidebarContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <Link to="/admin">
                <SidebarMenuButton asChild isActive={pathname === '/admin'}>
                  <span>
                    <LayoutDashboard />
                    Dashboard
                  </span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <Link to="/admin/listings">
                <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/listings')}>
                  <span>
                    <List />
                    Manage Listings
                  </span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <Link to="/admin/super-admin">
                <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/super-admin')}>
                  <span>
                    <Users />
                    User Management
                  </span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <Link to="/admin/subscriptions">
                <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/subscriptions')}>
                  <span>
                    <CreditCard />
                    Subscriptions
                  </span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
            <SidebarGroup>
              <SidebarGroupLabel>Communications</SidebarGroupLabel>
              <SidebarMenuItem>
                <Link to="/admin/inquiries">
                  <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/inquiries')}>
                    <span>
                      <MessageSquare />
                      Inquiries
                    </span>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <Link to="/admin/contacts">
                  <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/contacts')}>
                    <span>
                      <Mail />
                      Contact Messages
                    </span>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
            </SidebarGroup>
            <SidebarGroup>
              <SidebarGroupLabel>Business</SidebarGroupLabel>
              <SidebarMenuItem>
                <Link to="/admin/joint-ventures">
                  <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/joint-ventures')}>
                    <span>
                      <Handshake />
                      Joint Ventures
                    </span>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <Link to="/admin/finance-applications">
                      <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/finance-applications')}>
                        <span>
                          <DollarSign />
                          Finance Applications
                        </span>
                      </SidebarMenuButton>
                    </Link>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                <Link to="/admin/payments">
                  <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/payments')}>
                    <span>
                      <CreditCard />
                      Payments
                    </span>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <Link to="/admin/marketing">
                  <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/marketing')}>
                    <span>
                      <Megaphone />
                      Marketing
                    </span>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <Link to="/admin/social-campaigns">
                  <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/social-campaigns')}>
                    <span>
                      <Share2 />
                      Social Campaigns
                    </span>
                  </SidebarMenuButton>
                  </Link>
                </SidebarMenuItem>
              <SidebarMenuItem>
                <Link to="/admin/analytics">
                  <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/analytics')}>
                    <span>
                      <BarChart />
                      Analytics
                    </span>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
            </SidebarGroup>
            <SidebarGroup>
              <SidebarGroupLabel>System</SidebarGroupLabel>
              <SidebarMenuItem>
                <Link to="/admin/data-center">
                  <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/data-center')}>
                    <span>
                      <Database />
                      Data Center
                    </span>
                  </SidebarMenuButton>
                </Link>

              </SidebarMenuItem>
              <SidebarMenuItem>
                <Link to="/admin/content">
                  <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/content')}>
                    <span>
                      <FileText />
                      Content
                    </span>
                  </SidebarMenuButton>
                </Link>

              </SidebarMenuItem>
              <SidebarMenuItem>
                <Link to="/admin/settings">
                  <SidebarMenuButton asChild isActive={pathname.startsWith('/admin/settings')}>
                    <span>
                      <Settings />
                      Settings
                    </span>
                  </SidebarMenuButton>
                </Link>

              </SidebarMenuItem>
            </SidebarGroup>
          </SidebarMenu>
        </SidebarContent>
        <SidebarFooter>
          <div className="flex items-center gap-2 p-2 border-t bg-gray-900 text-green-400 border-gray-800">
            <Avatar>
              <AvatarImage src={user.photoURL || undefined} />
              <AvatarFallback>{user.email?.[0].toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-grow">
              <p className="text-sm font-medium text-white">{user.fullName || "Admin User"}</p>
              <p className="text-xs text-green-400">{user.email}</p>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleLogout} 
              className="text-green-400 hover:bg-gray-700 hover:text-white"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset>
        <header className="flex h-14 items-center gap-4 border-b bg-background/95 px-6 sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <SidebarTrigger className="md:hidden" />
          <div className="flex-1"></div>
          <Link to="/">
            <Button variant="outline">Back to Site</Button>
          </Link>
        </header>
        <main className="flex-1 p-6">
          {children}
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}